import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/components/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, Loader2 } from 'lucide-react';
import { AUTH } from '@/constants/testIds';
import { toast } from 'sonner';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password);
    setLoading(false);

    if (result.success) {
      toast.success('Welcome back!');
      // Auto-redirect handled by PaywallGate on /dashboard
      navigate('/dashboard');
    } else {
      toast.error(result.error || 'Login failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6 paper-texture">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <BookOpen className="w-8 h-8 text-[#6B7A4F]" />
            <span className="text-2xl font-bold text-[#2D2A26]">Bookish Bestie</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-light text-[#2D2A26] mb-2">
            Welcome <span className="font-bold">Back</span>
          </h1>
          <p className="text-[#5C5955]">Sign in to continue your reading journey</p>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#6B7A4F] mt-3 opacity-70">
            A Built for Bookish Experience
          </p>
        </div>

        <div className="glass-card p-8 rounded-2xl space-y-6">
          <form data-testid={AUTH.loginForm} onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#2D2A26] font-medium">
                Email
              </Label>
              <Input
                id="email"
                data-testid={AUTH.loginEmailInput}
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-white/50 border-[#E1D9CD] focus:border-[#6B7A4F] focus:ring-2 focus:ring-[#6B7A4F]/20 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#2D2A26] font-medium">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-[#6B7A4F] hover:underline"
                  data-testid="auth-forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                data-testid={AUTH.loginPasswordInput}
                type="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="bg-white/50 border-[#E1D9CD] focus:border-[#6B7A4F] focus:ring-2 focus:ring-[#6B7A4F]/20 rounded-xl"
              />
            </div>

            <Button
              data-testid={AUTH.loginSubmitBtn}
              type="submit"
              disabled={loading}
              className="w-full bg-[#6B7A4F] hover:bg-[#57653F] text-white py-6 rounded-full text-base font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="text-center">
            <p className="text-[#5C5955]">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="text-[#6B7A4F] font-semibold hover:underline"
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-[#5C5955] hover:text-[#6B7A4F] transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
